import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  StockCount,
  StockCountListItem,
  StockCountStatus,
} from "./inventory.type";
import { IngredientApi } from "./ingredient.api";
import { StockAdjustmentApi } from "./stockAdjustment.api";

export interface StockCountLinePatch {
  lineUuid: string;
  countedQuantity?: string | null;
  countedPackaging?: string | null;
  countedPackageQuantity?: string | null;
}

export interface StockCountPatchPayload {
  notes?: string | null;
  counts?: StockCountLinePatch[];
}

export interface GetStockCountsParams {
  page?: number;
  limit?: number;
  status?: StockCountStatus;
}

export const StockCountApi = createApi({
  reducerPath: "stockCountApi",
  tagTypes: ["stockCount"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getStockCounts: build.query<
      PaginationResults<StockCountListItem>,
      { restaurantId: string; query?: GetStockCountsParams }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/stock_counts`,
        method: "GET",
        params: query,
      }),
      providesTags: ["stockCount"],
      transformResponse: (data: CollectionData<StockCountListItem>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getStockCount: build.query<StockCount, { uuid: string }>({
      query: ({ uuid }) => `stock_counts/${uuid}`,
      providesTags: ["stockCount"],
    }),

    createStockCount: build.mutation<
      StockCount,
      { restaurantId: string; data: { notes?: string } }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/stock_counts`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["stockCount"],
    }),

    updateStockCount: build.mutation<
      StockCount,
      { uuid: string; data: StockCountPatchPayload }
    >({
      query: ({ uuid, data }) => ({
        url: `stock_counts/${uuid}`,
        method: "PATCH",
        headers: { "Content-Type": "application/merge-patch+json" },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["stockCount"],
    }),

    deleteStockCount: build.mutation<void, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `stock_counts/${uuid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["stockCount"],
    }),

    finalizeStockCount: build.mutation<StockCount, { uuid: string }>({
      query: ({ uuid }) => ({
        url: `stock_counts/${uuid}/finalize`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["stockCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
          dispatch(
            StockAdjustmentApi.util.invalidateTags(["stockAdjustment"])
          );
        } catch {
          // ignore
        }
      },
    }),
  }),
});

export const {
  useGetStockCountsQuery,
  useGetStockCountQuery,
  useCreateStockCountMutation,
  useUpdateStockCountMutation,
  useDeleteStockCountMutation,
  useFinalizeStockCountMutation,
} = StockCountApi;
